import { LoadingOutlined } from '@ant-design/icons'
import { Button, FieldInput, Form, Label, Modal, Spin, Typography, usePrevTheme } from '@prev/ui-kit'
import React, { useContext, useEffect, useState } from 'react'
import { RecoverySuccessFeedback } from './RecoverySuccessFeedback'
import { LoginContext } from '../../contexts/LoginContext'
import { emailOuCPFRegex } from '@/utils/constants'

const { Text } = Typography

interface ForgotPasswordModalProps {
  isOpenRecovery: boolean
  setIsOpenRecovery: (prop: boolean) => void
  slug: string | null | undefined
}

interface FormRecovery {
  username: string
}

export const ForgotPasswordModal = (props: ForgotPasswordModalProps): JSX.Element => {
  const [form] = Form.useForm<FormRecovery>()
  const { isOpenRecovery, setIsOpenRecovery, slug } = props
  const { theme } = usePrevTheme()
  const { passwordRecovery } = useContext(LoginContext)
  const [success, setSuccess] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const handleClose = (): void => {
    setIsOpenRecovery(false)
  }

  useEffect(() => {
    form.resetFields()
  }, [isOpenRecovery, success])

  const handleConfirm = async (data: { username: string }): Promise<void> => {
    try {
      setIsLoading(true)
      await passwordRecovery({
        username: data.username,
        slug: slug ?? null,
      })
      setIsLoading(false)
      setSuccess(true)
      setIsOpenRecovery(false)
    } catch (err: unknown) {
      setIsLoading(false)
    }
  }

  return (
    <>
      <Modal
        data-testid="modal-esqueceu-senha"
        open={isOpenRecovery}
        forceRender
        closeIcon={false}
        onCancel={!isLoading ? handleClose : undefined}
        styles={{
          content: { padding: '24px', backgroundColor: theme.color.surface.container.default },
          header: { backgroundColor: theme.color.surface.container.default },
        }}
        width={480}
        title={<Text emphasis>Esqueceu sua senha ?</Text>}
        footer={[
          <Button
            key="cancelar"
            disabled={isLoading}
            type="subtle"
            data-testid="btn-cancelar"
            style={{ color: theme.color.text.default }}
            onClick={handleClose}
          >
            Cancelar
          </Button>,
          <Button
            key="enviar"
            type="primary"
            data-testid="btn-enviar"
            style={{ color: '#FDFDFF', opacity: isLoading ? 0.65 : 1 }}
            onClick={() => {
              if (!isLoading) {
                form.submit()
              }
            }}
          >
            {isLoading && <Spin data-testid="loading-recuperacao" style={{ color: '#FDFDFF' }} indicator={<LoadingOutlined />} />}
            <Text style={{ color: '#FDFDFF' }}>Enviar</Text>
          </Button>,
        ]}
      >
        <Text color={theme.color.text.subtle} style={{ lineHeight: '120%', marginTop: '16px' }}>
          Fique tranquilo (a), você pode cadastrar uma nova senha. Basta digitar seu email cadastrado neste escritório e
          enviaremos um e-mail com um link para você cadastrar uma nova senha.
        </Text>
        <Text emphasis color={theme.color.text.subtle} data-testid="slug-escritorio" style={{ marginBottom: '8px' }}>
          https://{slug}.prev.app
        </Text>
        <Form data-testid="form-esqueceu-senha" form={form} onFinish={handleConfirm}>
          <Label>
            Email ou CPF <span style={{ color: theme.color.text.danger }}>*</span>
            <Form.Item
              name="username"
              validateDebounce={500}
              rules={[
                {
                  required: true,
                  pattern: emailOuCPFRegex,
                  message: 'Email ou CPF inválido',
                },
              ]}
            >
              <FieldInput data-testid="input-email-cpf" placeholder="Digite seu email ou CPF" />
            </Form.Item>
          </Label>
        </Form>
      </Modal>
      <RecoverySuccessFeedback handleSuccess={setSuccess} handleOfficeModal={setIsOpenRecovery} success={success} />
    </>
  )
}
